'use client'

/**
 * Stage 43 — одна строка примеров сценариев 6–7, раскрытая целиком.
 *
 * ЗАЧЕМ
 * Под причиной «Необычная передача между участниками» стоит число, а под ним пятьдесят
 * строк по десять колонок. Аналитик видел закономерность, но не мог проверить ни одной
 * записи: названий отправителя, получателя и плательщика, БИН, пограничных переходов,
 * внутренних станций и точного времени приёма и раскредитования в строке нет и не будет.
 * У сценариев 1–5 такая карточка есть («Детали исторической перевозки»); эта — её ровно
 * такой же аналог для 6–7, с той же вёрсткой и теми же правилами.
 *
 * ЧЕМ ОНА ОТЛИЧАЕТСЯ ОТ КАРТОЧКИ 1–5
 * Там строка — ПАРА, у неё две ноги, и карточка сравнивает их между собой. Здесь строка —
 * ОДНА НОГА корпуса: один вагон или контейнер под одним номером отправки, на одной
 * стороне. Второй ноги у неё нет, поэтому нет ни блока «Что изменилось», ни интервала —
 * достроить их было бы вымыслом.
 *
 * ЗАГОЛОВОК — «запись корпуса», а не «кейс»
 * Большинство этих записей кейсом не являются вовсе: их не взяла ни одна кандидатная пара
 * сценариев 6–7. Кнопка «Открыть кейс» появляется ТОЛЬКО когда ровно одна пара называет
 * эту ногу своей и эта пара есть в витрине. Несколько пар — кнопки нет: выбрать одну из
 * конкурирующих связей значило бы выдать догадку за факт.
 *
 * ЧТО ОНА НЕ ДЕЛАЕТ
 * Не считает, не сверяет коды со справочниками и не заполняет пустое поле соседним.
 * Отсутствующее значение печатается прочерком — отсутствие данных тоже факт.
 */
import { useEffect } from 'react'
import Link from 'next/link'
import { useEvidenceRowDetail } from '../cross-unit.hooks'
import { etsngRowsFor } from '@/lib/etsng-classification'
import type { EvidenceRowLeg, ExampleCells } from '../cross-unit.evidence-examples'
import type { HistoryDetailContext } from '../../history-detail.types'

/* ── примитивы (те же, что в карточке 1–5) ───────────────────────────────────── */

const DASH = <span className="text-[var(--text-tertiary)]">—</span>

function Row({ k, v, mono, title }: {
  k: string; v: string | null | undefined; mono?: boolean; title?: string
}) {
  return (
    <div className="grid grid-cols-[minmax(0,150px)_1fr] items-baseline gap-2 py-px">
      <dt className="text-[11px] leading-tight text-[var(--text-tertiary)]">{k}</dt>
      <dd
        className={`min-w-0 break-words text-[12px] leading-tight text-[var(--text-secondary)] ${mono ? 'font-mono tabular-nums' : ''}`}
        title={title ?? (v || undefined)}
      >
        {v || DASH}
      </dd>
    </div>
  )
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-[3px] border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-3">
      <h4 className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-secondary)]">
        {title}
      </h4>
      <dl>{children}</dl>
    </section>
  )
}

/** «66.460 т» — масса как есть; отсутствующая масса НЕ становится нулём. */
function weight(v: number | null | undefined): string | null {
  return typeof v === 'number' && Number.isFinite(v) ? `${v.toFixed(3)} т` : null
}

function ru(iso: string | null | undefined): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? ''))
  return m ? `${m[3]}.${m[2]}.${m[1]}` : null
}

function ruTime(ts: string | null | undefined): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}:\d{2}:\d{2})/.exec(String(ts ?? ''))
  if (m) return `${m[3]}.${m[2]}.${m[1]} ${m[4]}`
  return ru(ts)
}

const SIDE_TITLE: Record<string, string> = {
  import: 'Импортная нога',
  export: 'Экспортная нога',
}

/* ── блоки ───────────────────────────────────────────────────────────────────── */

function LegBlock({ leg, side }: { leg: EvidenceRowLeg; side: string | null }) {
  const unitLabel = leg.unitType === 'container' ? 'Контейнер' : 'Вагон'
  return (
    <Block title={SIDE_TITLE[String(side)] ?? 'Нога перевозки'}>
      <Row k="Номер отправки" v={leg.shipmentId} mono />
      <Row k={unitLabel} v={leg.wagonNumber ?? leg.containerNumber} mono />
      <Row k="Груз" v={leg.cargoName} />
      <Row k="ЕТСНГ" v={leg.cargoEtsng} mono />
      {/* Классификация ЕТСНГ ЭТОЙ ноги — тот же общий справочник, что и в карточках
          кейса. Пары у записи корпуса нет, поэтому и сравнения импорт→экспорт здесь
          быть не может: достраивать вторую сторону было бы вымыслом. */}
      {etsngRowsFor(leg.cargoEtsng).map((r) => (
        <Row key={r.key} k={r.label} v={r.value} />
      ))}
      <Row k="ГНГ" v={leg.cargoGng} mono />
      <Row k="Масса" v={weight(leg.weightTons)} mono />
      {/* Названия участников и плательщика приходят точными или не приходят вовсе. */}
      <Row k="Отправитель" v={leg.senderName} />
      <Row k="БИН отправителя" v={leg.senderBin} mono />
      <Row k="Получатель" v={leg.receiverName} />
      <Row k="БИН получателя" v={leg.receiverBin} mono />
      <Row k="Плательщик" v={leg.payerName} />
      <Row k="Код плательщика" v={leg.payerCode} mono />
    </Block>
  )
}

/**
 * Маршрут: то, чего в одной колонке таблицы не помещается.
 *
 * Промежуточные точки не достраиваются — печатается ровно то, что несёт запись.
 */
function RouteBlock({ leg }: { leg: EvidenceRowLeg }) {
  return (
    <Block title="Маршрут">
      <Row k="Страна отправления" v={leg.countryFrom} />
      <Row k="Станция отправления" v={leg.stationFrom} />
      <Row k="Станция операции" v={leg.stationOp} />
      <Row k="Станция назначения" v={leg.stationTo} />
      <Row k="Страна назначения" v={leg.countryTo} />
      <Row k="Пункт пересечения границы" v={leg.borderIn ?? leg.borderOut} />
      <Row k="Маршрут в источнике" v={leg.routeText} />
    </Block>
  )
}

/** Время операций — то, чего в строке таблицы нет вовсе. */
function TimesBlock({ leg }: { leg: EvidenceRowLeg }) {
  return (
    <Block title="Время операций">
      <Row k="Отправление" v={ruTime(leg.departureAt)} mono />
      <Row k="Операция" v={ruTime(leg.operationAt)} mono />
      <Row k="Прибытие" v={ruTime(leg.arrivalAt)} mono />
      <Row k="Приём груза" v={ruTime(leg.acceptAt)} mono />
      <Row k="Выдача" v={ruTime(leg.deliveryAt)} mono />
      <Row k="Раскредитование" v={ruTime(leg.creditAt)} mono />
    </Block>
  )
}

/** Почему эта запись показана — из метаданных того набора, из которого её открыли. */
function ContextBlock({ ctx, participant, role }: {
  ctx: HistoryDetailContext; participant: string | null; role: string | null
}) {
  return (
    <Block title="Почему эта запись показана">
      <Row k="Источник" v={ctx.title} />
      <Row k="Отбор" v={ctx.selectionDescription} />
      <Row k="Ключ отбора" v={participant} mono />
      <Row k="Роль в этой записи" v={role} />
      {ctx.variantLabel && <Row k="Группа вариантов" v={ctx.variantLabel} mono />}
    </Block>
  )
}

/* ── карточка ────────────────────────────────────────────────────────────────── */

export function EvidenceRowDetail({ row, context, onClose }: {
  row: ExampleCells
  context: HistoryDetailContext
  onClose: () => void
}) {
  const { data, isLoading, isError } = useEvidenceRowDetail(row.sourceUid)

  // Escape закрывает только эту карточку: обработчик карточки кейса под ней получит
  // событие лишь после того, как этот слой снимется.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose() }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onClose])

  const leg = data?.detailStatus === 'ok' ? data.leg : null
  const kase = data?.case
  // Кейс предлагается ТОЛЬКО при доказанной единственной паре, которая есть в витрине.
  // `userSurfaceEligible === null` (витрина не отвечала) ссылкой не считается: мёртвая
  // ссылка хуже её отсутствия.
  const openable = kase?.status === 'exact' && !!kase.canonicalPairId && kase.userSurfaceEligible === true

  return (
    <div
      data-block="evidence-row-detail"
      role="dialog"
      aria-modal="true"
      aria-label="Детали записи корпуса"
      className="fixed inset-0 z-[60] flex justify-end bg-black/40"
      onClick={onClose}
    >
      <div
        className="flex h-full w-full max-w-[720px] flex-col overflow-y-auto border-l border-[var(--border-default)] bg-[var(--bg-canvas)]"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-[var(--border-default)] bg-[var(--bg-surface)] px-4 py-3">
          <div className="min-w-0">
            <h3 className="text-[13px] font-semibold text-[var(--text-primary)]">
              Детали записи корпуса
            </h3>
            <p className="mt-0.5 truncate font-mono text-[11px] text-[var(--text-tertiary)]">
              {[row.date, row.sideLabel, row.shipment].filter(Boolean).join(' · ') || '—'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрыть детали записи корпуса"
            className="shrink-0 rounded-[3px] px-2 py-1 text-[13px] text-[var(--text-secondary)] hover:bg-[var(--bg-subtle)]"
          >
            ✕
          </button>
        </header>

        {isLoading && (
          <div data-state="loading" className="space-y-2 p-4">
            <p className="text-[11px] text-[var(--text-tertiary)]">Загрузка деталей записи…</p>
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-20 animate-pulse rounded-[3px] bg-[var(--bg-subtle)]" />
            ))}
          </div>
        )}

        {!isLoading && (isError || !leg) && (
          <div data-state="error" className="p-4">
            <p className="text-[12px] leading-relaxed text-[var(--text-secondary)]">
              Не удалось загрузить детали этой записи.
            </p>
            <p className="mt-1 text-[10px] leading-relaxed text-[var(--text-tertiary)]">
              {data?.detailStatus === 'corpus_row_unavailable'
                ? 'Запись индекса найдена, но исходная строка замороженного корпуса не прочиталась. '
                  + 'Показать приблизительно похожую запись вместо неё нельзя.'
                : 'Слой подробностей сейчас недоступен.'}
            </p>
          </div>
        )}

        {leg && (
          <div data-state="ready" className="space-y-3 p-4">
            <section className="rounded-[3px] border border-[var(--border-subtle)] bg-[var(--bg-subtle)] p-3">
              <dl className="grid grid-cols-2 gap-x-4">
                <Row k="Дата события" v={ru(data?.eventDate)} mono />
                <Row k="Сторона" v={row.sideLabel} />
                <Row k="Номер отправки" v={leg.shipmentId} mono />
                <Row k="Вагон / контейнер" v={leg.wagonNumber ?? leg.containerNumber} mono />
              </dl>
              {/* Одна нога — не перевозка целиком. Сказано прямо, чтобы карточку не
                  прочитали как «вот весь маршрут груза». */}
              <p className="mt-2 text-[10px] leading-relaxed text-[var(--text-tertiary)]">
                Это одна нога перевозки — один вагон или контейнер под одним номером отправки
                на одной стороне. Второй ноги у этой записи в корпусе нет.
              </p>
            </section>

            <ContextBlock ctx={context} participant={row.participant} role={row.participantRole} />

            <LegBlock leg={leg} side={data?.side ?? row.side} />
            <RouteBlock leg={leg} />
            <TimesBlock leg={leg} />

            {/* ── кейс: только доказанный и только один ─────────────────────── */}
            <Block title="Кейс сценариев 6–7">
              {kase?.status === 'exact' && (
                <>
                  <Row
                    k="Связь"
                    v={`эта нога — ${kase.legRole === 'export' ? 'экспортная' : 'импортная'} сторона одной кандидатной пары`}
                  />
                  <Row k="Идентификатор кейса" v={kase.canonicalPairId} mono />
                  <Row k="Сценарий" v={kase.scenarioId != null ? String(kase.scenarioId) : null} />
                  {openable ? (
                    <div className="mt-2">
                      <Link
                        href={`/cases/${kase.canonicalPairId}?scenario_id=${kase.scenarioId ?? 7}`}
                        data-action="open-case"
                        className="inline-block rounded-[3px] border border-[var(--border-default)] px-2 py-1 text-[11px] text-[var(--text-secondary)] hover:bg-[var(--bg-subtle)]"
                      >
                        Открыть полный кейс
                      </Link>
                    </div>
                  ) : (
                    <p className="mt-1.5 text-[10px] leading-relaxed text-[var(--text-tertiary)]">
                      {kase.userSurfaceEligible === false
                        ? 'Эта пара исключена из витрины кейсов (маршрут внутри ЕАЭС либо неполные страны), '
                          + 'поэтому открывать её ссылкой отсюда некуда.'
                        : 'Принадлежность к витрине кейсов подтвердить не удалось, поэтому ссылка не предлагается.'}
                    </p>
                  )}
                </>
              )}
              {kase?.status === 'ambiguous' && (
                <p className="text-[11px] leading-relaxed text-[var(--text-secondary)]">
                  Эту ногу заявляют {kase.candidateCount} конкурирующих кандидатных связей. Выбрать
                  одну из них произвольно нельзя, поэтому кейс отсюда не открывается.
                </p>
              )}
              {kase?.status === 'none' && (
                <p className="text-[11px] leading-relaxed text-[var(--text-secondary)]">
                  Это событие корпуса, а не кейс: ни одна кандидатная пара сценариев 6–7 не взяла
                  эту ногу. Число выше считает такие события, а не кейсы.
                </p>
              )}
            </Block>

            <p className="text-[10px] leading-relaxed text-[var(--text-tertiary)]">
              Поля прочитаны из той же замороженной выгрузки, по которой рассчитано число; там,
              где значения в источнике нет, стоит прочерк.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
